/**
 * Client configuration page — the re-homed Setup tab. One editing surface
 * for client profile/settings; the Dashboard modal and the Home tab both
 * folded into here. Saves refresh the store client (no full-page reload).
 */

import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAppStore } from "@/core/store/useAppStore";
import { clientService } from "@/domains/Client/Client.service";
import { PageLayout } from "@/components/PageLayout";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { useDetailTrail } from "@/core/navigation/TrailDisplayContext";
import { PortalAccessSection } from "./components/PortalAccessSection";
import { PushHealthSection } from "./components/PushHealthSection";
import { CaptionConfigEditor } from "./components/CaptionConfigEditor";
import { SocialMediaSection } from "./components/SocialMediaSection";
import { ClientProfileSection } from "./components/ClientProfileSection";
import { ClientSettingsSection } from "./components/ClientSettingsSection";
import { EditClientProfileModal } from "./components/EditClientProfileModal";
import { EditClientSettingsModal } from "./components/EditClientSettingsModal";
import { SocialProfilesSection } from "./components/SocialProfilesSection";

export const ClientSettingsView: React.FC = () => {
  const queryClient = useQueryClient();
  const { selectedClient, setSelectedClient } = useAppStore();
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  const clientId = selectedClient?.id ?? "";
  // Header trail: Home › Settings (or wherever a stamped flow came from).
  useDetailTrail({
    fallback: { to: `/app/${clientId}/home`, label: ENTITY_CONFIG.home.label },
    title: "Settings",
  });

  if (!selectedClient) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <p className="text-gray-500">Please select a client to view settings</p>
      </div>
    );
  }

  const client = selectedClient;

  const handleClientUpdated = async () => {
    setShowProfileModal(false);
    setShowSettingsModal(false);
    const updated = await clientService.getClientById(client.id);
    setSelectedClient(updated);
    queryClient.invalidateQueries({ queryKey: ["clients"] });
  };

  return (
    <PageLayout>
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="px-4 lg:px-6 py-6 space-y-5">
          {/* Identity + targets, then connections + access, then push
              health + caption config, side by side with equal row heights,
              then social profiles across the last row, so no cell sits
              empty beside it. No finance here
              (DD-039): the agency's own profile is set from the client's
              Finance page. */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
            <div className="bg-white rounded-2xl p-6 h-full">
              <ClientProfileSection client={client} onEdit={() => setShowProfileModal(true)} />
            </div>
            <div className="bg-white rounded-2xl p-6 h-full">
              <ClientSettingsSection client={client} onEdit={() => setShowSettingsModal(true)} />
            </div>
            <div className="bg-white rounded-2xl p-6 h-full">
              <SocialMediaSection client={client} />
            </div>
            <div className="bg-white rounded-2xl p-6 h-full">
              <PortalAccessSection client={client} />
            </div>
            <div className="bg-white rounded-2xl p-6 h-full">
              <PushHealthSection client={client} />
            </div>
            <div className="bg-white rounded-2xl p-6 h-full">
              <CaptionConfigEditor client={client} />
            </div>
            <div className="bg-white rounded-2xl p-6 h-full xl:col-span-2">
              <SocialProfilesSection client={client} />
            </div>
          </div>
        </div>
      </div>

      <EditClientProfileModal
        isOpen={showProfileModal}
        client={client}
        onClose={() => setShowProfileModal(false)}
        onClientUpdated={handleClientUpdated}
      />
      <EditClientSettingsModal
        isOpen={showSettingsModal}
        client={client}
        onClose={() => setShowSettingsModal(false)}
        onClientUpdated={handleClientUpdated}
      />
    </PageLayout>
  );
};
